import { useCallback } from "react";
import { powerPointTemplateService } from "@/lib/services/powerpoint-template-service";
import { ServiceResult } from "@/lib/services/base/service-types";
import type {
  TemplateScope,
  PowerPointTemplateResponse,
  TemplateListFilters,
  PowerPointTemplateUpdate,
  TemplatePlaceholderAnalysis,
  AvailableTemplatesResponse,
  TemplateUploadParams,
  PowerPointTemplateDeleteResponse,
  TemplateLoadResponse,
} from "@/lib/types/api/powerpoint-template-types";

/**
 * React hook for PowerPoint Template Service
 *
 * Provides a React-friendly interface to the PowerPointTemplateService
 * with proper error handling and state management integration.
 *
 * Follows the same patterns as other service hooks in the codebase.
 */
export function usePowerPointTemplateService() {
  const service = powerPointTemplateService;

  // Template CRUD operations
  const uploadTemplate = useCallback(
    async (
      params: TemplateUploadParams
    ): Promise<ServiceResult<PowerPointTemplateResponse>> => {
      return service.uploadTemplate(params);
    },
    []
  );

  const requestUploadUrl = useCallback(
    async (params: {
      display_name: string;
      file_size: number;
      content_type: string;
      template_name: string;
      description?: string;
      category?: string;
      scope: TemplateScope;
      agent_id?: string;
      shared_scope_id?: string;
      is_default?: boolean;
      is_public?: boolean;
    }): Promise<
      ServiceResult<{
        upload_url: string;
        s3_path: string;
        template_metadata: Record<string, any>;
      }>
    > => {
      return service.requestUploadUrl(params);
    },
    []
  );

  const completeUpload = useCallback(
    async (
      s3_path: string,
      template_metadata: Record<string, any>
    ): Promise<ServiceResult<PowerPointTemplateResponse>> => {
      return service.completeUpload(s3_path, template_metadata);
    },
    []
  );

  const getTemplates = useCallback(
    async (
      filters?: TemplateListFilters
    ): Promise<ServiceResult<PowerPointTemplateResponse[]>> => {
      return service.getTemplates(filters);
    },
    []
  );

  const getAvailableTemplates = useCallback(
    async (
      agentId?: string,
      sharedScopeId?: string
    ): Promise<ServiceResult<AvailableTemplatesResponse>> => {
      return service.getAvailableTemplates(agentId, sharedScopeId);
    },
    []
  );

  const getDefaultTemplate = useCallback(
    async (
      scope: string,
      agentId?: string,
      sharedScopeId?: string
    ): Promise<ServiceResult<PowerPointTemplateResponse>> => {
      return service.getDefaultTemplate(scope as any, agentId, sharedScopeId);
    },
    []
  );

  const getTemplate = useCallback(
    async (
      templateId: string
    ): Promise<ServiceResult<PowerPointTemplateResponse>> => {
      return service.getTemplate(templateId);
    },
    []
  );

  const updateTemplate = useCallback(
    async (
      templateId: string,
      updates: PowerPointTemplateUpdate
    ): Promise<ServiceResult<PowerPointTemplateResponse>> => {
      return service.updateTemplate(templateId, updates);
    },
    []
  );

  const deleteTemplate = useCallback(
    async (
      templateId: string,
      hardDelete: boolean = false
    ): Promise<ServiceResult<PowerPointTemplateDeleteResponse>> => {
      return service.deleteTemplate(templateId, hardDelete);
    },
    []
  );

  // Template loading
  const loadTemplateForProcessing = useCallback(
    async (
      templateName: string,
      agentId?: string,
      sharedScopeId?: string
    ): Promise<ServiceResult<TemplateLoadResponse>> => {
      return service.loadTemplateForProcessing(
        templateName,
        agentId,
        sharedScopeId
      );
    },
    []
  );

  return {
    // CRUD operations
    uploadTemplate,
    requestUploadUrl,
    completeUpload,
    getTemplates,
    getAvailableTemplates,
    getDefaultTemplate,
    getTemplate,
    updateTemplate,
    deleteTemplate,

    // Processing
    loadTemplateForProcessing,
  };
}
